import { useEffect, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useSearchParameters } from '@/components/signup/hooks/use-search-parameters';
import { isJsmPlaygroundSignup } from '@/components/signup/utils/jsm-playground-signup-intent';
import {
	type IBxpWebStargateClient,
	useBxpWebStargateClient,
} from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import type { JiraProjectSummary } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';

import { useCloudStatus } from '../../hooks/use-cloud-status';
import {
	USER_SEGMENTATION_TEAM_TYPES,
	UserSegmentationTeamTypes,
} from '../../models/user-segmentation';
import {
	CSM_PROJECT_TEMPLATE_BLUEPRINT_KEY,
	JSM_TEMPLATE_ID_TO_BLUEPRINT_KEY,
} from '../jsm-use-case-template-choice/jsm-use-case-template-choice-constants';
import { getProjectKeyFromTeamType } from '../setup/jira/utils';

import {
	getJsmUseCaseProjectFailure,
	type JsmUseCaseProjectFailure,
} from './get-jsm-use-case-project-failure';
import {
	abandonProjectCreationLease,
	completeProjectCreationLease,
	coordinateProjectCreation,
	createProjectCreationOwnerId,
	failProjectCreationLease,
	markProjectCreationLeaseCreating,
	type ProjectCreationLeaseHandle,
} from './jsm-project-creation-lease';
import { jsmUseCaseProvisioningMessages } from './jsm-use-case-provisioning-messages';
import {
	getProjectRecoveryStrategy,
	recoverJsmUseCaseProject,
} from './recover-jsm-use-case-project';

const JIRA_TEMPLATE_READINESS_POLL_INTERVAL_MS = 5_000;
const JSM_TEMPLATE_READINESS_TIMEOUT_MS = 30_000;
const CSM_TEMPLATE_READINESS_TIMEOUT_MS = 60_000;
const JIRA_PROJECT_LOOKUP_RETRY_TIMEOUT_MS = 15_000;
const JSM_LAND_ONBOARDING_ORIGIN_SOURCE = 'jsmLandOnboarding';
const JSM_TEMPLATE_KEY_NAMESPACE = 'com.atlassian.servicedesk:';
const INVALID_PROJECT_READINESS_FAILURE = {
	errorMessage: 'Project readiness response is invalid',
};

const JSM_PLAYGROUND_TEMPLATE_KEY_SUFFIXES: Readonly<Record<string, readonly string[]>> = {
	'com.atlassian.servicedesk:company-managed-general-service-project': ['general-service-project'],
	'com.atlassian.servicedesk:simplified-analytics-service-desk': ['analytics-service-desk'],
	'com.atlassian.servicedesk:simplified-design-service-desk': ['design-service-desk'],
	'com.atlassian.servicedesk:simplified-development-requests-service-desk': [
		'development-requests-service-desk',
		'product-requests-service-desk',
	],
	'com.atlassian.servicedesk:simplified-external-service-desk': [
		'external-service-desk',
		'sales-service-desk',
	],
	'com.atlassian.servicedesk:simplified-finance-service-desk': ['finance-service-desk'],
	'com.atlassian.servicedesk:simplified-hr-service-desk': ['hr-service-desk'],
	'com.atlassian.servicedesk:simplified-it-service-management-basic': [
		'it-service-management-basic',
		'it-service-desk',
	],
	'com.atlassian.servicedesk:simplified-legal-service-desk': ['legal-service-desk'],
	'com.atlassian.servicedesk:simplified-marketing-service-desk': ['marketing-service-desk'],
};

type ResolveJiraProjectTemplateKeyOptions = {
	readonly advertisedTemplateKeys: readonly string[];
	readonly blueprintKey: string;
	readonly isPlaygroundSignup: boolean;
};

export const resolveJiraProjectTemplateKey = ({
	advertisedTemplateKeys,
	blueprintKey,
	isPlaygroundSignup,
}: ResolveJiraProjectTemplateKeyOptions): string | undefined => {
	if (advertisedTemplateKeys.includes(blueprintKey)) {
		return blueprintKey;
	}
	if (!isPlaygroundSignup) {
		return undefined;
	}

	const semanticSuffixes = JSM_PLAYGROUND_TEMPLATE_KEY_SUFFIXES[blueprintKey];
	if (!semanticSuffixes) {
		return undefined;
	}

	return advertisedTemplateKeys.find((advertisedTemplateKey) => {
		if (!advertisedTemplateKey.startsWith(JSM_TEMPLATE_KEY_NAMESPACE)) {
			return false;
		}
		const templateKey = advertisedTemplateKey.slice(JSM_TEMPLATE_KEY_NAMESPACE.length);
		return semanticSuffixes.some(
			(semanticSuffix) =>
				templateKey === semanticSuffix || templateKey.endsWith(`-${semanticSuffix}`),
		);
	});
};

const getDepartmentType = (teamType: string): string =>
	teamType.replace(/-([a-z])/g, (_match, character: string) => character.toUpperCase());

const getTemplateReadinessConfig = (blueprintKey: string) =>
	blueprintKey === CSM_PROJECT_TEMPLATE_BLUEPRINT_KEY
		? {
				timeoutErrorMessage: 'Customer Service Management project template readiness timed out',
				timeoutMs: CSM_TEMPLATE_READINESS_TIMEOUT_MS,
			}
		: {
				timeoutErrorMessage: 'Jira project template readiness timed out',
				timeoutMs: JSM_TEMPLATE_READINESS_TIMEOUT_MS,
			};

type GetTemplateKeysForBlueprintOptions = {
	readonly blueprintKey: string;
	readonly cloudId: string;
	readonly stargateClient: Pick<
		IBxpWebStargateClient,
		'getJiraCreatableProjectTemplateKeys' | 'getJiraSimplifiedProjectTemplateKeys'
	>;
};

const getTemplateKeysForBlueprint = ({
	blueprintKey,
	cloudId,
	stargateClient,
}: GetTemplateKeysForBlueprintOptions): (() => Promise<readonly string[]>) | undefined => {
	const getTemplateKeys =
		blueprintKey === CSM_PROJECT_TEMPLATE_BLUEPRINT_KEY
			? stargateClient.getJiraCreatableProjectTemplateKeys
			: stargateClient.getJiraSimplifiedProjectTemplateKeys;

	return getTemplateKeys ? () => getTemplateKeys.call(stargateClient, { cloudId }) : undefined;
};

type GetFeatureGatedProjectFailureOptions = {
	readonly error: unknown;
	readonly isBugfixEnabled: boolean;
	readonly projectKey: string;
	readonly projectName: string;
};

const getFeatureGatedProjectFailure = async ({
	error,
	isBugfixEnabled,
	projectKey,
	projectName,
}: GetFeatureGatedProjectFailureOptions): Promise<JsmUseCaseProjectFailure> => {
	if (!isBugfixEnabled) {
		return { errorMessage: error instanceof Error ? error.message : 'Unknown error' };
	}

	return getJsmUseCaseProjectFailure({ error, projectKey, projectName });
};

type WaitForJiraTemplateReadinessOptions = {
	readonly blueprintKey: string;
	readonly getTemplateKeys: () => Promise<readonly string[]>;
	readonly isCancelled: () => boolean;
	readonly isPlaygroundSignup: boolean;
	readonly timeoutMs: number;
	readonly waitForNextCheck: (delay: number) => Promise<void>;
};

const waitForJiraTemplateReadiness = async ({
	blueprintKey,
	getTemplateKeys,
	isCancelled,
	isPlaygroundSignup,
	timeoutMs,
	waitForNextCheck,
}: WaitForJiraTemplateReadinessOptions): Promise<string | undefined> => {
	const deadline = Date.now() + timeoutMs;

	while (!isCancelled()) {
		try {
			const templateKeys = await getTemplateKeys();
			const resolvedTemplateKey = resolveJiraProjectTemplateKey({
				advertisedTemplateKeys: templateKeys,
				blueprintKey,
				isPlaygroundSignup,
			});
			if (resolvedTemplateKey) {
				return resolvedTemplateKey;
			}
		} catch {
			// Jira may still be starting. Retry readiness checks until the deadline.
		}
		if (isCancelled()) {
			return undefined;
		}

		const remainingTime = deadline - Date.now();
		if (remainingTime <= 0) {
			return undefined;
		}

		await waitForNextCheck(Math.min(JIRA_TEMPLATE_READINESS_POLL_INTERVAL_MS, remainingTime));
	}

	return undefined;
};

type ProjectPreparationResult =
	| { readonly kind: 'cancelled' }
	| { readonly kind: 'ready'; readonly templateKey: string }
	| { readonly kind: 'existing'; readonly project: JiraProjectSummary }
	| {
			readonly errorMessage: string;
			readonly kind: 'failure';
			readonly status: JsmUseCaseProjectStatus;
	  };

type PrepareJiraProjectCreationOptions = {
	readonly blueprintKey: string;
	readonly getProject: (() => Promise<JiraProjectSummary | undefined>) | undefined;
	readonly getTemplateKeys: (() => Promise<readonly string[]>) | undefined;
	readonly isCancelled: () => boolean;
	readonly isPlaygroundSignup: boolean;
	readonly waitForNextCheck: (delay: number) => Promise<void>;
};

const prepareJiraProjectCreation = async ({
	blueprintKey,
	getProject,
	getTemplateKeys,
	isCancelled,
	isPlaygroundSignup,
	waitForNextCheck,
}: PrepareJiraProjectCreationOptions): Promise<ProjectPreparationResult> => {
	const { timeoutErrorMessage, timeoutMs } = getTemplateReadinessConfig(blueprintKey);

	if (getProject) {
		try {
			const project = await getProject();
			if (isCancelled()) {
				return { kind: 'cancelled' };
			}
			if (project) {
				return { kind: 'existing', project };
			}
		} catch {
			// Jira may still be starting. Retry the mandatory lookup after template readiness.
		}
	}

	if (!getTemplateKeys) {
		return {
			errorMessage: 'Jira project template readiness method is unavailable',
			kind: 'failure',
			status: 'error',
		};
	}

	const resolvedTemplateKey = await waitForJiraTemplateReadiness({
		blueprintKey,
		getTemplateKeys,
		isCancelled,
		isPlaygroundSignup,
		timeoutMs,
		waitForNextCheck,
	});
	if (isCancelled()) {
		return { kind: 'cancelled' };
	}
	if (!resolvedTemplateKey) {
		return {
			errorMessage: timeoutErrorMessage,
			kind: 'failure',
			status: 'timeout',
		};
	}
	if (!getProject) {
		return {
			errorMessage: 'Jira project lookup method is unavailable',
			kind: 'failure',
			status: 'error',
		};
	}

	const deadline = Date.now() + JIRA_PROJECT_LOOKUP_RETRY_TIMEOUT_MS;
	while (!isCancelled()) {
		try {
			const project = await getProject();
			if (isCancelled()) {
				return { kind: 'cancelled' };
			}
			return project
				? { kind: 'existing', project }
				: { kind: 'ready', templateKey: resolvedTemplateKey };
		} catch {
			const remainingTime = deadline - Date.now();
			if (remainingTime <= 0) {
				return {
					errorMessage: 'Jira project lookup failed',
					kind: 'failure',
					status: 'error',
				};
			}
			await waitForNextCheck(Math.min(JIRA_TEMPLATE_READINESS_POLL_INTERVAL_MS, remainingTime));
		}
	}
	return { kind: 'cancelled' };
};

type HandleProjectPreparationOptions = {
	readonly preparation: ProjectPreparationResult;
	readonly setFailure: (errorMessage: string, status: JsmUseCaseProjectStatus) => void;
};

const shouldStopAfterProjectPreparation = ({
	preparation,
	setFailure,
}: HandleProjectPreparationOptions): boolean => {
	if (preparation.kind === 'ready') {
		return false;
	}
	if (preparation.kind === 'failure') {
		setFailure(preparation.errorMessage, preparation.status);
	}
	return true;
};

const recordProjectPreparationLeaseResult = (
	preparation: ProjectPreparationResult,
	handle: ProjectCreationLeaseHandle,
): void => {
	if (preparation.kind === 'existing') {
		completeProjectCreationLease(handle, preparation.project);
	}
	if (preparation.kind === 'failure') {
		failProjectCreationLease(handle, { errorMessage: preparation.errorMessage });
	}
};

type AcquirePreparedProjectCreationLeaseOptions = PrepareJiraProjectCreationOptions & {
	readonly cloudId: string;
	readonly completeProject: (project: JiraProjectSummary, attemptId?: string) => boolean;
	readonly ownerId: string;
	readonly onStarted: (handle: ProjectCreationLeaseHandle) => void;
	readonly onSucceeded: (outcome: 'existing' | 'resumed', attemptId: string) => void;
	readonly onSuppressed: (handle: ProjectCreationLeaseHandle) => void;
	readonly projectKey: string;
	readonly setFailure: (
		failure: JsmUseCaseProjectFailure,
		status: JsmUseCaseProjectStatus,
		attemptId: string | undefined,
	) => void;
};

type PreparedProjectCreationLease = {
	readonly handle: ProjectCreationLeaseHandle;
	readonly stopHeartbeat: () => void;
	readonly templateKey: string;
};

const acquirePreparedProjectCreationLease = async ({
	cloudId,
	completeProject,
	ownerId,
	onStarted,
	onSucceeded,
	onSuppressed,
	projectKey,
	setFailure,
	...preparationOptions
}: AcquirePreparedProjectCreationLeaseOptions): Promise<
	PreparedProjectCreationLease | undefined
> => {
	const coordination = await coordinateProjectCreation({
		cloudId,
		getProject: preparationOptions.getProject,
		isCancelled: preparationOptions.isCancelled,
		ownerId,
		onFollower: onSuppressed,
		projectKey,
		waitForNextCheck: preparationOptions.waitForNextCheck,
	});
	if (coordination.kind === 'success') {
		if (completeProject(coordination.project, coordination.attemptId)) {
			onSucceeded('resumed', coordination.attemptId);
		}
		return undefined;
	}
	if (coordination.kind === 'failure') {
		setFailure(
			coordination.failure,
			getProjectStatusForFailure(coordination.failure),
			coordination.attemptId,
		);
		return undefined;
	}
	if (coordination.kind === 'cancelled') {
		return undefined;
	}
	if (preparationOptions.isCancelled()) {
		coordination.stopHeartbeat();
		abandonProjectCreationLease(coordination.handle);
		return undefined;
	}
	onStarted(coordination.handle);

	const preparation = await prepareJiraProjectCreation(preparationOptions);
	if (
		preparation.kind === 'existing' &&
		!completeProject(preparation.project, coordination.handle.attemptId)
	) {
		failProjectCreationLease(coordination.handle, INVALID_PROJECT_READINESS_FAILURE);
		coordination.stopHeartbeat();
		return undefined;
	}
	recordProjectPreparationLeaseResult(preparation, coordination.handle);
	if (preparation.kind === 'existing') {
		onSucceeded('existing', coordination.handle.attemptId);
	}
	const shouldStop = shouldStopAfterProjectPreparation({
		preparation,
		setFailure: (errorMessage, status) =>
			setFailure({ errorMessage }, status, coordination.handle.attemptId),
	});
	if (shouldStop) {
		coordination.stopHeartbeat();
		if (preparation.kind === 'cancelled') {
			abandonProjectCreationLease(coordination.handle);
		}
		return undefined;
	}
	if (preparation.kind !== 'ready') {
		return undefined;
	}
	return {
		handle: coordination.handle,
		stopHeartbeat: coordination.stopHeartbeat,
		templateKey: preparation.templateKey,
	};
};

type ProjectCreationResult =
	| { readonly kind: 'cancelled' }
	| { readonly error: unknown; readonly kind: 'failure' }
	| { readonly kind: 'created' | 'recovered'; readonly project: JiraProjectSummary };

type CreateOrRecoverJiraProjectOptions = {
	readonly createProject: () => Promise<JiraProjectSummary>;
	readonly getProject: (() => Promise<JiraProjectSummary | undefined>) | undefined;
	readonly isBugfixEnabled: boolean;
	readonly isCancelled: () => boolean;
	readonly waitForNextCheck: (delay: number) => Promise<void>;
};

const createOrRecoverJiraProject = async ({
	createProject,
	getProject,
	isBugfixEnabled,
	isCancelled,
	waitForNextCheck,
}: CreateOrRecoverJiraProjectOptions): Promise<ProjectCreationResult> => {
	try {
		const project = await createProject();
		return isCancelled() ? { kind: 'cancelled' } : { kind: 'created', project };
	} catch (error) {
		if (isCancelled()) {
			return { kind: 'cancelled' };
		}
		if (!isBugfixEnabled || !getProject) {
			return { error, kind: 'failure' };
		}

		const project = await recoverJsmUseCaseProject({
			getProject,
			isCancelled,
			strategy: getProjectRecoveryStrategy(error),
			waitForNextCheck,
		});
		if (isCancelled()) {
			return { kind: 'cancelled' };
		}
		return project ? { kind: 'recovered', project } : { error, kind: 'failure' };
	}
};

type FinishJiraProjectCreationOptions = CreateOrRecoverJiraProjectOptions & {
	readonly completeProject: (project: JiraProjectSummary, attemptId?: string) => boolean;
	readonly leaseHandle: ProjectCreationLeaseHandle | undefined;
	readonly onCreated: (project: JiraProjectSummary) => void;
	readonly onSucceeded: (outcome: 'created' | 'recovered', attemptId: string | undefined) => void;
	readonly projectKey: string;
	readonly projectName: string;
	readonly setFailure: (failure: JsmUseCaseProjectFailure) => void;
};

const finishJiraProjectCreation = async ({
	completeProject,
	isBugfixEnabled,
	leaseHandle,
	onCreated,
	onSucceeded,
	projectKey,
	projectName,
	setFailure,
	...creationOptions
}: FinishJiraProjectCreationOptions): Promise<void> => {
	const creation = await createOrRecoverJiraProject({
		...creationOptions,
		isBugfixEnabled,
	});
	if (creation.kind === 'cancelled') {
		return;
	}
	if (creation.kind === 'failure') {
		const failure = await getFeatureGatedProjectFailure({
			error: creation.error,
			isBugfixEnabled,
			projectKey,
			projectName,
		});
		setFailure(failure);
		if (leaseHandle) {
			failProjectCreationLease(leaseHandle, failure);
		}
		return;
	}

	if (!completeProject(creation.project, leaseHandle?.attemptId)) {
		if (leaseHandle) {
			failProjectCreationLease(leaseHandle, INVALID_PROJECT_READINESS_FAILURE);
		}
		return;
	}
	if (leaseHandle) {
		completeProjectCreationLease(leaseHandle, creation.project);
	}
	if (creation.kind === 'created') {
		onCreated(creation.project);
	}
	onSucceeded(creation.kind, leaseHandle?.attemptId);
};

export type JsmUseCaseProjectStatus =
	| 'idle'
	| 'waiting-for-readiness'
	| 'creating'
	| 'complete'
	| 'error'
	| 'timeout';

const getProjectStatusForFailure = (
	failure: Pick<JsmUseCaseProjectFailure, 'errorMessage' | 'httpStatus'>,
): JsmUseCaseProjectStatus =>
	failure.httpStatus === 408 ||
	failure.errorMessage.includes('timed out') ||
	failure.errorMessage.includes('timeout')
		? 'timeout'
		: 'error';

const getDefaultProjectNameMessage = (teamType: string) => {
	if (USER_SEGMENTATION_TEAM_TYPES.includes(teamType as UserSegmentationTeamTypes)) {
		return jsmUseCaseProvisioningMessages.defaultProjectName[teamType as UserSegmentationTeamTypes];
	}
	return jsmUseCaseProvisioningMessages.defaultProjectNameFallback;
};

export type UseJsmUseCaseProjectCreateReturn = {
	isSimplifiedProject: boolean | undefined;
	projectLifecycle: ProjectCreationLifecycle | undefined;
	projectErrorMessage: string | undefined;
	projectFailure: JsmUseCaseProjectFailure | undefined;
	projectKey: string | undefined;
	projectStatus: JsmUseCaseProjectStatus;
};

export type ProjectCreationLifecycle = {
	readonly attemptId: string | undefined;
	readonly elapsedTimeMs: number;
	readonly errorCategory: ProjectErrorCategory | undefined;
	readonly failurePhase: string | undefined;
	readonly viewInstanceId: string;
};

export type ProjectErrorCategory =
	| 'authentication'
	| 'authorization'
	| 'client'
	| 'conflict'
	| 'local'
	| 'network'
	| 'rate-limit'
	| 'server'
	| 'timeout'
	| 'unknown'
	| 'validation';

export const getProjectErrorCategory = (
	failure: JsmUseCaseProjectFailure,
	status: JsmUseCaseProjectStatus,
): ProjectErrorCategory => {
	if (status === 'timeout') {
		return 'timeout';
	}
	switch (failure.httpStatus) {
		case 400:
		case 404:
		case 422:
			return 'validation';
		case 401:
			return 'authentication';
		case 403:
			return 'authorization';
		case 408:
			return 'timeout';
		case 409:
			return 'conflict';
		case 429:
			return 'rate-limit';
	}
	if (failure.httpStatus && failure.httpStatus >= 500) {
		return 'server';
	}
	if (failure.httpStatus) {
		return 'client';
	}
	if (/failed to fetch|network|load failed/i.test(failure.errorMessage)) {
		return 'network';
	}
	if (/unavailable|lookup failed|cloud creation failed|readiness/i.test(failure.errorMessage)) {
		return 'local';
	}
	return 'unknown';
};

type UseJsmUseCaseProjectCreateParams = {
	templateId: string | undefined;
	teamType: string | undefined;
	requireReliableProvisioning?: boolean;
};

export const useJsmUseCaseProjectCreate = ({
	templateId,
	teamType,
	requireReliableProvisioning = false,
}: UseJsmUseCaseProjectCreateParams): UseJsmUseCaseProjectCreateReturn => {
	const intl = useIntl();
	const { spaceName } = useSearchParameters();
	const { cloudId, createCloudStatus } = useCloudStatus();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const stargateClient = useBxpWebStargateClient();
	const isPlaygroundSignup = useMemo(() => isJsmPlaygroundSignup(window.location.search), []);
	const hasCreatedRef = useRef(false);
	const reliableProvisioningRef = useRef(requireReliableProvisioning);
	if (!hasCreatedRef.current) {
		reliableProvisioningRef.current = requireReliableProvisioning;
	}
	// A gate change must not cancel an already-started project attempt and lose its response.
	const requiresReliableProvisioning = reliableProvisioningRef.current;
	const isReliableProvisioningEnabled = useMemo(
		() =>
			requiresReliableProvisioning ||
			isPlaygroundSignup ||
			checkFeatureFlagGate(featureFlags.jsmLandUseCaseOnboardingBugfix),
		[isPlaygroundSignup, requiresReliableProvisioning],
	);
	const resolvedProjectKey = teamType ? getProjectKeyFromTeamType(teamType) : undefined;
	const defaultProjectName = teamType
		? intl.formatMessage(getDefaultProjectNameMessage(teamType))
		: undefined;
	const resolvedProjectName = spaceName?.trim() || defaultProjectName;
	const blueprintKey = templateId
		? (JSM_TEMPLATE_ID_TO_BLUEPRINT_KEY[templateId] ?? templateId)
		: undefined;

	const ownerIdRef = useRef<string>();
	const ownerId = (ownerIdRef.current ??= createProjectCreationOwnerId());
	const lifecycleStartedAtRef = useRef(Date.now());

	const [projectStatus, setProjectStatus] = useState<JsmUseCaseProjectStatus>('idle');
	const [projectErrorMessage, setProjectErrorMessage] = useState<string | undefined>();
	const [projectFailure, setProjectFailure] = useState<JsmUseCaseProjectFailure | undefined>();
	const [projectLifecycle, setProjectLifecycle] = useState<ProjectCreationLifecycle | undefined>();
	const [projectKey, setProjectKey] = useState<string | undefined>();
	const [isSimplifiedProject, setIsSimplifiedProject] = useState<boolean | undefined>();

	useEffect(() => {
		let isCancelled = false;
		let hasSubmittedCreateRequest = false;
		let pollingTimer: ReturnType<typeof setTimeout> | undefined;
		let resolvePollingTimer: (() => void) | undefined;
		let stopLeaseHeartbeat: (() => void) | undefined;
		let leaseHandle: ProjectCreationLeaseHandle | undefined;

		const waitForNextCheck = (delay: number): Promise<void> =>
			new Promise((resolve) => {
				const completeWait = () => {
					pollingTimer = undefined;
					resolvePollingTimer = undefined;
					resolve();
				};

				resolvePollingTimer = completeWait;
				pollingTimer = setTimeout(completeWait, delay);
			});
		const setLocalProjectFailure = (errorMessage: string) => {
			setProjectErrorMessage(errorMessage);
			setProjectFailure(isReliableProvisioningEnabled ? { errorMessage } : undefined);
		};
		const setLifecycleFailure = (
			failure: JsmUseCaseProjectFailure,
			status: JsmUseCaseProjectStatus,
			failurePhase: string,
			attemptId?: string,
		) => {
			if (!isReliableProvisioningEnabled) {
				return;
			}
			setProjectLifecycle({
				attemptId,
				elapsedTimeMs: Date.now() - lifecycleStartedAtRef.current,
				errorCategory: getProjectErrorCategory(failure, status),
				failurePhase,
				viewInstanceId: ownerId,
			});
		};
		const fireLifecycleEvent = (
			action: 'started' | 'succeeded' | 'suppressed',
			attemptId: string,
			outcome?: 'created' | 'existing' | 'recovered' | 'resumed',
		) => {
			if (!isReliableProvisioningEnabled) {
				return;
			}
			createAnalyticsEvent({
				eventType: 'operational',
				actionSubject: 'jsmUseCaseProjectCreate',
				action,
				attributes: {
					attemptId,
					elapsedTimeMs: Date.now() - lifecycleStartedAtRef.current,
					isJSMOnboarding: true,
					isServiceCollectionEnabled: true,
					outcome,
					projectKey: resolvedProjectKey,
					selectedTemplateId: templateId,
					teamType,
					templateKey: blueprintKey,
					viewInstanceId: ownerId,
				},
			}).fire();
		};
		const completeProject = (project: JiraProjectSummary, attemptId?: string): boolean => {
			if (
				requiresReliableProvisioning &&
				(!project ||
					project.projectKey !== resolvedProjectKey ||
					!Number.isSafeInteger(project.projectId) ||
					project.projectId <= 0 ||
					typeof project.simplified !== 'boolean')
			) {
				setLocalProjectFailure(INVALID_PROJECT_READINESS_FAILURE.errorMessage);
				setProjectStatus('error');
				setLifecycleFailure(INVALID_PROJECT_READINESS_FAILURE, 'error', 'readiness', attemptId);
				return false;
			}
			setProjectKey(project.projectKey);
			setIsSimplifiedProject(project.simplified);
			setProjectStatus('complete');
			return true;
		};
		const hasCloudCreationFailed =
			createCloudStatus === 'error' || createCloudStatus === 'error-unrecoverable';
		const hasCloudCreationTimedOut =
			createCloudStatus === 'timeout' || createCloudStatus === 'timeout-recoverable';

		if (hasCloudCreationFailed || hasCloudCreationTimedOut) {
			const errorMessage = hasCloudCreationTimedOut
				? 'Cloud creation timed out'
				: 'Cloud creation failed';
			setLocalProjectFailure(errorMessage);
			const status = hasCloudCreationTimedOut ? 'timeout' : 'error';
			setProjectStatus(status);
			setLifecycleFailure({ errorMessage }, status, 'cloud');
			return;
		}

		if (
			!cloudId ||
			!blueprintKey ||
			!resolvedProjectKey ||
			!resolvedProjectName ||
			!teamType ||
			!templateId ||
			createCloudStatus !== 'success' ||
			hasCreatedRef.current
		) {
			return;
		}

		hasCreatedRef.current = true;

		const createProject = async () => {
			setProjectErrorMessage(undefined);
			let resolvedTemplateKey = blueprintKey;

			const postProject = stargateClient.postJiraSimplifiedProjectCreate?.bind(stargateClient);
			if (!postProject) {
				const errorMessage = 'Project create method is unavailable';
				setLocalProjectFailure(errorMessage);
				setProjectStatus('error');
				setLifecycleFailure({ errorMessage }, 'error', 'create');
				return;
			}
			const getProjectByKey = stargateClient.getJiraProjectByKey?.bind(stargateClient);
			const getProject = getProjectByKey
				? () => getProjectByKey({ cloudId, projectKey: resolvedProjectKey })
				: undefined;
			if (isReliableProvisioningEnabled) {
				const getTemplateKeys = getTemplateKeysForBlueprint({
					blueprintKey,
					cloudId,
					stargateClient,
				});
				setProjectStatus('waiting-for-readiness');
				const preparedLease = await acquirePreparedProjectCreationLease({
					blueprintKey,
					cloudId,
					completeProject,
					getProject,
					getTemplateKeys,
					isCancelled: () => isCancelled,
					isPlaygroundSignup,
					ownerId,
					onStarted: (handle) => fireLifecycleEvent('started', handle.attemptId),
					onSucceeded: (outcome, attemptId) => fireLifecycleEvent('succeeded', attemptId, outcome),
					onSuppressed: (handle) => fireLifecycleEvent('suppressed', handle.attemptId),
					projectKey: resolvedProjectKey,
					setFailure: (failure, status, attemptId) => {
						setProjectErrorMessage(failure.errorMessage);
						setProjectFailure(failure);
						setProjectStatus(status);
						const failurePhase = failure.errorMessage.includes('readiness')
							? 'readiness'
							: 'preflight';
						setLifecycleFailure(failure, status, failurePhase, attemptId);
					},
					waitForNextCheck,
				});
				if (!preparedLease) {
					return;
				}
				leaseHandle = preparedLease.handle;
				stopLeaseHeartbeat = preparedLease.stopHeartbeat;
				resolvedTemplateKey = preparedLease.templateKey;
			}

			setProjectStatus('creating');
			try {
				await finishJiraProjectCreation({
					completeProject,
					createProject: () => {
						if (leaseHandle) {
							markProjectCreationLeaseCreating(leaseHandle);
						}
						hasSubmittedCreateRequest = true;
						return postProject({
							cloudId,
							key: resolvedProjectKey,
							name: resolvedProjectName,
							templateKey: resolvedTemplateKey,
						});
					},
					getProject,
					isBugfixEnabled: isReliableProvisioningEnabled,
					isCancelled: () => isCancelled,
					leaseHandle,
					onCreated: (project) =>
						createAnalyticsEvent({
							eventType: 'track',
							product: 'jira',
							action: 'created',
							actionSubject: 'project',
							containerId: String(project.projectId),
							containerType: 'project',
							attributes: {
								categoryType: ['serviceManagement'],
								createdUsing: 'JsmUseCaseProvisioningScreen',
								departmentType: getDepartmentType(teamType),
								isAdmin: true,
								isConnectedProjectScenario: false,
								isContainerLinkingSelected: false,
								isJSMOnboarding: true,
								isLicensedProduct: true,
								isOnboarding: true,
								isProjectAdmin: true,
								isServiceCollectionEnabled: true,
								originSource: JSM_LAND_ONBOARDING_ORIGIN_SOURCE,
								productType: 'serviceManagement',
								simplified: project.simplified,
								teamType,
								templateKey: resolvedTemplateKey,
								templateType: templateId,
								userSpecifiedProjectKey: false,
							},
						}).fire(),
					onSucceeded: (outcome, attemptId) => {
						if (attemptId) {
							fireLifecycleEvent('succeeded', attemptId, outcome);
						}
					},
					projectKey: resolvedProjectKey,
					projectName: resolvedProjectName,
					setFailure: (failure) => {
						setProjectErrorMessage(failure.errorMessage);
						setProjectFailure(isReliableProvisioningEnabled ? failure : undefined);
						const status = getProjectStatusForFailure(failure);
						setProjectStatus(status);
						setLifecycleFailure(failure, status, 'recovery', leaseHandle?.attemptId);
					},
					waitForNextCheck,
				});
			} finally {
				stopLeaseHeartbeat?.();
				stopLeaseHeartbeat = undefined;
			}
		};

		void createProject();

		return () => {
			isCancelled = true;
			if (!hasSubmittedCreateRequest) {
				hasCreatedRef.current = false;
			}
			stopLeaseHeartbeat?.();
			if (leaseHandle) {
				abandonProjectCreationLease(leaseHandle);
			}
			if (pollingTimer !== undefined) {
				clearTimeout(pollingTimer);
			}
			resolvePollingTimer?.();
		};
	}, [
		blueprintKey,
		cloudId,
		createAnalyticsEvent,
		createCloudStatus,
		isPlaygroundSignup,
		isReliableProvisioningEnabled,
		ownerId,
		resolvedProjectKey,
		resolvedProjectName,
		requiresReliableProvisioning,
		stargateClient,
		teamType,
		templateId,
	]);

	return {
		isSimplifiedProject,
		projectErrorMessage,
		projectFailure,
		projectKey,
		projectLifecycle,
		projectStatus,
	};
};
